import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

const Breadcrumb = () => {
  const location = useLocation();
  const pathnames = useMemo(() => location.pathname.split('/').filter(x => x), [location.pathname]);
  const [clubName, setClubName] = useState(null);
  const [sessionName, setSessionName] = useState(null);
  
  // Check if we came from a specific context (e.g., from homepage club management)
  const fromManageClubs = location.state?.fromManageClubs;
  const fromMyClubs = location.state?.fromMyClubs;

  /** Club admin drill-down routes that sit under Management in the hierarchy. */
  const CLUB_ADMIN_SECTIONS = new Set([
    'sessions',
    'session-wrap-up',
    'start-session',
    'session-simulations',
    'settings',
    'club-settings',
    'payment-tracking',
    'tournaments',
    'league-squad',
    'league-performance',
  ]);

  const shouldShowClubManagementCrumb = (section, clubIndex) => {
    if (!section) return false;
    if (CLUB_ADMIN_SECTIONS.has(section)) return true;
    return section === 'sessions' && pathnames[clubIndex + 3] === 'setup';
  };

  // Define route labels
  const routeLabels = {
    'upcoming-sessions': 'Sessions',
    'player-dashboard': 'Dashboard',
    'club-admin': 'Admin',
    'session-management': 'Session Management',
    'manage-clubs': 'Manage Clubs',
    'club-management': 'Club Management',
    'club-analysis': 'Club Analysis',
    'my-clubs': 'My Clubs',
    'create-club': 'Create Club',
    'find-club': 'Find Club',
    'profile': 'Player Hub',
    'clubs': 'Clubs',
    'leagues': 'Leagues',
    'middlesex': 'Middlesex League',
    'league-admin': 'League admin',
  };

  // Fetch club name if we're on a club page
  useEffect(() => {
    const fetchClubName = async () => {
      // Check if we're on a club page (path like /club/:clubId, /club-management/:clubId, or /profile/clubs/:clubId)
      const clubIndex = pathnames.findIndex(path => path === 'club' || path === 'club-management');
      const clubsIndex = pathnames.findIndex(path => path === 'clubs');
      
      let clubId = null;
      
      // Handle /club/:clubId or /club-management/:clubId
      if (clubIndex !== -1 && pathnames[clubIndex + 1]) {
        clubId = pathnames[clubIndex + 1];
      }
      // Handle /clubs/:clubId (public club page) or /profile/clubs/:clubId
      else if (clubsIndex !== -1 && pathnames[clubsIndex + 1]) {
        // Check if it's a UUID (club ID) - UUIDs are typically 36 characters with dashes
        const nextPath = pathnames[clubsIndex + 1];
        if (nextPath.length > 10) { // Likely a UUID (club ID)
          clubId = nextPath;
        }
      }
      
      if (clubId) {
        try {
          const { data: club, error } = await supabase
            .from('clubs')
            .select('name')
            .eq('id', clubId)
            .single();
          
          if (!error && club && club.name !== clubName) {
            setClubName(club.name);
          } else if (error && clubName) {
            // Only clear if we had a name before
            setClubName(null);
          }
        } catch (err) {
          console.error('Error fetching club name:', err);
        }
      } else {
        // Only update if clubName is not already null
        if (clubName !== null) {
        setClubName(null);
        }
      }
    };

    fetchClubName();
  }, [pathnames]);

  // Fetch session name for live session routes and player performance pages.
  useEffect(() => {
    const fetchSessionInfo = async () => {
      const sessionIndex = pathnames.indexOf('session');

      if (sessionIndex !== -1 && pathnames.length >= sessionIndex + 2) {
        const sessionId = pathnames[sessionIndex + 1];

        try {
          const { data: sessionData, error: sessionError } = await supabase
            .from('sessions')
            .select(`
              name,
              clubs:club_id (
                id,
                name
              )
            `)
            .eq('id', sessionId)
            .single();

          if (!sessionError && sessionData) {
            if (sessionData.name !== sessionName) {
              setSessionName(sessionData.name);
            }
            if (sessionData.clubs && sessionData.clubs.name !== clubName) {
              setClubName(sessionData.clubs.name);
            }
          }
        } catch (err) {
          console.error('Error fetching session info:', err);
        }
      } else if (sessionName !== null) {
        setSessionName(null);
      }
    };

    fetchSessionInfo();
  }, [pathnames]);

  // Don't show breadcrumb on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="hidden md:block bg-surface border-b border-border-medium shadow-brand-sm" aria-label="Breadcrumb">
      <div className="px-3 sm:px-6 md:px-8 lg:px-10 py-2 md:py-3.5">
        <ol className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <li className="shrink-0">
            <Link 
              to="/" 
              className="text-text-muted hover:text-text-primary flex items-center transition-colors"
              aria-label="Home"
            >
              <svg className="w-4 h-4 md:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="hidden md:inline">Home</span>
            </Link>
          </li>
          
          {(() => {
            // Custom breadcrumb logic based on navigation context
            const breadcrumbItems = [];

            // Determine if we're on a club hub (/club-management/:clubId)
            const clubManagementIndex = pathnames.indexOf('club-management');
            const isOnClubHub = clubManagementIndex !== -1 && pathnames.length >= clubManagementIndex + 2;

            // Determine if we're inside a club section (/club/:clubId/<section>)
            const clubIndex = pathnames.indexOf('club');
            const isInClubSection = clubIndex !== -1 && pathnames.length >= clubIndex + 2;

            if (isOnClubHub) {
              const clubId = pathnames[clubManagementIndex + 1];

              // Home > Clubs > [Club Name] > Management
              breadcrumbItems.push({ label: 'Clubs', path: '/clubs', isLast: false });
              if (clubName) {
                breadcrumbItems.push({
                  label: clubName,
                  path: `/clubs/${clubId}`,
                  isLast: false
                });
              }
              breadcrumbItems.push({
                label: 'Management',
                path: `/club-management/${clubId}`,
                isLast: true
              });
            } else if (isInClubSection) {
              const clubId = pathnames[clubIndex + 1];
              const section = pathnames[clubIndex + 2]; // sessions | session-wrap-up | start-session | session-simulations | settings
              const returnToDashboard = location.state?.returnToDashboard;

              // Determine if we should show Clubs breadcrumb
              const fromProfile = location.state?.fromMyClubs || location.pathname.includes('/profile');
              
              // Home > Clubs > [Club Name] > Management > [Section]
              if (!fromProfile) {
                breadcrumbItems.push({ label: 'Clubs', path: '/clubs', isLast: false });
              } else {
                breadcrumbItems.push({ 
                  label: routeLabels['profile'] || 'Profile', 
                  path: '/profile', 
                  isLast: false 
                });
                breadcrumbItems.push({ 
                  label: routeLabels['clubs'] || 'My Clubs', 
                  path: '/profile/clubs', 
                  isLast: false 
                });
              }
              
              if (clubName) {
                // Link to Club Dashboard
                breadcrumbItems.push({
                  label: clubName,
                  path: `/clubs/${clubId}`,
                  isLast: false
                });
              }
              
              // Admin drill-down pages always show Management in the trail
              if (shouldShowClubManagementCrumb(section, clubIndex) || returnToDashboard) {
                breadcrumbItems.push({
                  label: 'Management',
                  path: `/club-management/${clubId}`,
                  isLast: false
                });
              }
              
              if (section === 'session' && pathnames.length > clubIndex + 3) {
                breadcrumbItems.push({
                  label: sessionName || 'Live Session',
                  path: location.pathname,
                  isLast: true,
                });
              } else if (section === 'sessions' && pathnames[clubIndex + 3]) {
                const subLabels = {
                  setup: 'Block bookings, series, and templates',
                  start: 'Start session',
                  review: 'Review sessions',
                };
                const sub = pathnames[clubIndex + 3];
                const hasBlockDetail = sub === 'setup' && pathnames[clubIndex + 4];
                breadcrumbItems.push({
                  label: subLabels[sub] || 'Plan sessions',
                  path: hasBlockDetail ? `/club/${clubId}/sessions/setup` : location.pathname,
                  isLast: !hasBlockDetail,
                });
                if (hasBlockDetail) {
                  breadcrumbItems.push({
                    label: 'Block sessions',
                    path: location.pathname,
                    isLast: true,
                  });
                }
              } else if (section) {
                const sectionLabelMap = {
                  sessions: 'Plan sessions',
                  'session-wrap-up': 'Review sessions',
                  'start-session': 'Start session',
                  'session-simulations': 'Session Simulations',
                  settings: 'Player Management',
                  templates: 'Templates',
                  'payment-tracking': 'Payment Tracking',
                  'club-settings': 'Club Settings',
                  'session-wrapup': 'Session wrap-up',
                  'league-squad': 'League squad',
                  'league-performance': 'Player performance',
                };
                const sectionLabel = sectionLabelMap[section] || (section ? section.charAt(0).toUpperCase() + section.slice(1).replace(/-/g, ' ') : '');
                breadcrumbItems.push({
                  label: sectionLabel,
                  path: `/club/${clubId}/${section}`,
                  isLast: true
                });
              }
            } else {
              // Handle profile/clubs routes specially
              const profileIndex = pathnames.indexOf('profile');
              const clubsIndex = pathnames.indexOf('clubs');
              
              if (profileIndex !== -1 && clubsIndex !== -1 && clubsIndex === profileIndex + 1) {
                // We're in /profile/clubs section
                breadcrumbItems.push({ 
                  label: routeLabels['profile'] || 'Profile', 
                  path: '/profile', 
                  isLast: false 
                });
                
                // Check if we're on /profile/clubs/:clubId (club detail page)
                if (pathnames.length > clubsIndex + 1) {
                  // We have a clubId, so show "My Clubs" > "Club Overview"
                  breadcrumbItems.push({ 
                    label: routeLabels['clubs'] || 'My Clubs', 
                    path: '/profile/clubs', 
                    isLast: false 
                  });
                  breadcrumbItems.push({ 
                    label: 'Club Overview', 
                    path: `/profile/clubs/${pathnames[clubsIndex + 1]}`, 
                    isLast: true 
                  });
                } else {
                  // We're on /profile/clubs (list page)
                  breadcrumbItems.push({ 
                    label: routeLabels['clubs'] || 'My Clubs', 
                    path: '/profile/clubs', 
                    isLast: true 
                  });
                }
              } else {
                // Handle /clubs/:clubId (Club Dashboard) specially
                const clubsIndex = pathnames.indexOf('clubs');
                if (clubsIndex !== -1 && pathnames.length > clubsIndex + 1) {
                  // We're on /clubs/:clubId - show "Clubs" > [Club Name]
                  // Check if we came from profile (My Clubs)
                  const fromProfile = location.state?.fromMyClubs || location.pathname.includes('/profile');
                  if (fromProfile) {
                    breadcrumbItems.push({ 
                      label: routeLabels['profile'] || 'Profile', 
                      path: '/profile', 
                      isLast: false 
                    });
                    breadcrumbItems.push({ 
                      label: routeLabels['clubs'] || 'My Clubs', 
                      path: '/profile/clubs', 
                      isLast: false 
                    });
                  } else {
                    breadcrumbItems.push({ 
                      label: routeLabels['clubs'] || 'Clubs', 
                      path: '/clubs', 
                      isLast: false 
                    });
                  }
                  // Add club name if we have it (fetched by useEffect)
                  if (clubName) {
                    breadcrumbItems.push({ 
                      label: clubName, 
                      path: `/clubs/${pathnames[clubsIndex + 1]}`, 
                      isLast: true 
                    });
                  }
                } else {
                  // Handle club-analysis route specially
                  const analysisIndex = pathnames.indexOf('club-analysis');
                  if (analysisIndex !== -1) {
                    const returnToDashboard = location.state?.returnToDashboard;
                    const clubId = location.state?.clubId;
                    
                    // Home > Clubs > [Club Name] > Management > Club Analysis
                    breadcrumbItems.push({ 
                      label: routeLabels['clubs'] || 'Clubs', 
                      path: '/clubs', 
                      isLast: false 
                    });
                    
                    if (clubName && clubId) {
                      breadcrumbItems.push({
                        label: clubName,
                        path: `/clubs/${clubId}`,
                        isLast: false
                      });
                    }
                    
                    // Show "Management" if navigated from the management page
                    if (returnToDashboard && clubId) {
                      breadcrumbItems.push({
                        label: 'Management',
                        path: `/club-management/${clubId}`,
                        isLast: false
                      });
                    }
                    
                    breadcrumbItems.push({
                      label: routeLabels['club-analysis'] || 'Club Analysis',
                      path: '/club-analysis',
                      isLast: true
                    });
                  } else {
                    // Handle /player/:playerId/session/:sessionId route
                    const playerIndex = pathnames.indexOf('player');
                    const sessionIndex = pathnames.indexOf('session');
                    
                    if (playerIndex !== -1 && sessionIndex !== -1) {
                      const clubId = location.state?.clubId || new URLSearchParams(location.search).get('clubId');
                      
                      // Home > Club Name > Performance > Session Name
                      if (clubName && clubId) {
                        breadcrumbItems.push({ 
                          label: clubName, 
                          path: `/clubs/${clubId}`, 
                          isLast: false 
                        });
                      }
                      
                      breadcrumbItems.push({ 
                        label: 'Performance', 
                        path: clubId ? `/clubs/${clubId}?tab=performance` : '#', 
                        isLast: false 
                      });
                      
                      if (sessionName) {
                        breadcrumbItems.push({ 
                          label: sessionName, 
                          path: location.pathname, 
                          isLast: true 
                        });
                      }
                } else if (pathnames[0] === 'session' && pathnames.length >= 2) {
                  breadcrumbItems.push({
                    label: sessionName || 'Session',
                    path: location.pathname,
                    isLast: true,
                  });
                } else {
                  // Default breadcrumb logic for other pages
                      // Skip UUIDs (IDs) - they're typically 36 characters with dashes
                  pathnames.forEach((pathname, index) => {
                        // Skip if it looks like a UUID (36 chars with dashes) or is very long (likely an ID)
                        if (pathname.length > 30 || (pathname.includes('-') && pathname.length > 20)) {
                          return; // Skip IDs
                        }
                        
                    const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
                    const isLast = index === pathnames.length - 1;

                    let label = routeLabels[pathname] || pathname.charAt(0).toUpperCase() + pathname.slice(1);
                    breadcrumbItems.push({ label, path: routeTo, isLast });
                  });
                    }
                  }
                }
              }
            }

            return breadcrumbItems.map((item, index) => (
              <li key={index} className="flex items-center shrink-0 min-w-0">
                <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-text-muted mx-1 md:mx-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                {item.isLast ? (
                  <span className="text-text-primary font-medium truncate max-w-[10rem] sm:max-w-none">{item.label}</span>
                ) : (
                  <Link 
                    to={item.path} 
                    className="text-text-muted hover:text-text-primary transition-colors truncate max-w-[6rem] sm:max-w-none"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ));
          })()}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;
